// components/breadCrumb/breadCrumb.tsx
import React from "react";
import { BiMenu } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { toggleSidebar } from '../../Redux/slices/ui';
import { useAppDispatch } from '../../Redux/store';
import { BREADCRUMB_MAP } from './breadCrumbMap';

interface BreadcrumbItem {
    label: string;
    path: string;
    icon: React.ElementType;
}

/**
 * BREADCRUMB_MAP with hierarchical structure
 * Section > Subsection > Current Page
 */


/**
 * Helper function to find breadcrumb path
 * Handles both exact paths and dynamic routes with parameters (e.g., /users/:id)
 */
const findBreadcrumbPath = (currentPath: string): BreadcrumbItem[] => {


    // First, try exact match
    if (BREADCRUMB_MAP[currentPath]) {
        return BREADCRUMB_MAP[currentPath];
    }

    // If no exact match, try to find a matching pattern
    // For example, /dashboard/users/123 should match /dashboard/users
    const pathSegments = currentPath.split('/').filter(Boolean); // Remove empty strings

    // Try to match by removing segments from the end until we find a match
    for (let i = pathSegments.length; i > 0; i--) {
        const testPath = '/' + pathSegments.slice(0, i).join('/');

        if (BREADCRUMB_MAP[testPath]) {
            const breadcrumbs = [...BREADCRUMB_MAP[testPath]];

            // If this is a deeper path than the matched one, add detail page
            if (i < pathSegments.length) {
                const lastSegment = pathSegments[pathSegments.length - 1];
                const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

                // If the last segment is an ID (number or UUID-like), add a "Details" page
                if (!isNaN(Number(lastSegment)) || lastSegment.length > 8) {
                    breadcrumbs.push({
                        label: `${lastBreadcrumb.label} details`,
                        path: currentPath,
                        icon: lastBreadcrumb.icon,
                    });
                }
            }

            return breadcrumbs;
        }
    }

    // If still no match, return empty array
    return [];
};

const Breadcrumb: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isDesktop = window.innerWidth > 850;
    const handleSidebarToggle = () => {
        if (!isDesktop) dispatch(toggleSidebar());
    };


    // Get breadcrumb items for current path
    const breadcrumbs = findBreadcrumbPath(location.pathname);

    // If no breadcrumbs found, return null
    if (breadcrumbs.length === 0) {
        return null;
    }

    const handleNavigate = (path: string) => {
        navigate(path);
    };

    return (
        <nav className="flex justify-between bg-white px-6 py-3 flex-shrink-0" aria-label="breadcrumb">
            <ol className="flex items-center gap-3 flex-wrap">
                {breadcrumbs.map((item, index) => {
                    const Icon = item.icon;
                    const isLast = index === breadcrumbs.length - 1;

                    return (
                        <li key={`${item.path}-${index}`} className="flex items-center gap-3">
                            <button
                                onClick={() => handleNavigate(item.path)}
                                className={twMerge(
                                    "flex items-center gap-2 text-sm font-medium transition-colors duration-200",
                                    isLast
                                        ? "text-primary cursor-default hover:text-primary"
                                        : "text-gray-600 hover:text-primary cursor-pointer"
                                )}
                            >
                                <Icon size={18} />
                                <span>{item.label}</span>
                            </button>

                            {/* Separator - only show if not last item */}
                            {!isLast && (
                                <span className="text-gray-400 text-lg">{">"}</span>
                            )}
                        </li>
                    );
                })}
            </ol>

            <div className="flex items-center cursor-pointer md:hidden">
                <BiMenu onClick={handleSidebarToggle} size={20} />
            </div>

        </nav>
    );
};

export default Breadcrumb;